// src/services/api.ts
import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { API_BASE_URL, API_URL } from '../config/apiConfig';
import { getAccessToken, setAccessToken, setUserData, clearAuth } from '../authStore';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    config.headers = config.headers || {};
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Renovacao de access token single-flight. Multiplos 401 concorrentes (rajada de
// registros) compartilham UMA unica chamada de refresh; a promise e limpa ao concluir
// para que uma expiracao posterior possa renovar de novo. O refresh usa fetch (nao o
// proprio api) para nao reentrar neste interceptor.
let refreshPromise: Promise<string | null> | null = null;

function refreshAccessToken(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const res = await fetch(`${API_URL}/authentication/token/refresh/`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });
        if (!res.ok) return null;
        const data = await res.json();
        if (data?.access) {
          setAccessToken(data.access);
          setUserData({ primeiro_nome: data.primeiro_nome ?? '', group: data.group ?? '' });
          return data.access as string;
        }
        return null;
      } catch {
        return null;
      }
    })();
    // Libera o single-flight so depois que todos os aguardando resolverem.
    refreshPromise.finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
    const status = error.response?.status;

    // Renova em 401, uma unica vez por requisicao, e nunca para o proprio refresh.
    if (status === 401 && original && !original._retry) {
      original._retry = true;
      const novoToken = await refreshAccessToken();
      if (novoToken) {
        original.headers = original.headers ?? {};
        original.headers.Authorization = `Bearer ${novoToken}`;
        return api(original);
      }
      // Refresh falhou: encerra a sessao (o AuthProvider redireciona ao detectar).
      clearAuth();
    }

    return Promise.reject(error);
  }
);

export default api;